import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $getRoot,
  $isRangeSelection,
  $createTextNode,
  FORMAT_TEXT_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import { TOGGLE_LINK_COMMAND, $isLinkNode } from "@lexical/link";
import Icon from "@mdi/react";
import {
  mdiFormatBold,
  mdiFormatItalic,
  mdiFormatUnderline,
  mdiFormatListNumbered,
  mdiFormatListBulleted,
  mdiLink,
  mdiImageOutline,
} from "@mdi/js";
import { $createVariableNode } from "./VariableNode";
import { INSERT_IMAGE_COMMAND } from "./ImagePlugin";
import VariableDropdown from "./VariableDropdown";
import InsertLinkModal from "./InsertLinkModal";
import { uploadTemplateImage } from "../slice";

const ToolbarPlugin = ({ onError }) => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkInitialText, setLinkInitialText] = useState("");
  const imageInputRef = useRef(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, updateToolbar]);

  const openLinkModal = () => {
    let selectedText = "";
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selectedText = selection.getTextContent();
      }
    });
    setLinkInitialText(selectedText);
    setLinkModalOpen(true);
  };

  const handleInsertLink = ({ url, text }) => {
    setLinkModalOpen(false);
    editor.update(() => {
      let selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        $getRoot().selectEnd();
        selection = $getSelection();
      }
      if (!$isRangeSelection(selection)) return;

      // If the caret sits inside an existing link, expand to the whole link
      // node and operate on that — otherwise a collapsed caret would nest a
      // new link inside the current one.
      const anchorNode = selection.anchor.getNode();
      const linkNode = $isLinkNode(anchorNode)
        ? anchorNode
        : anchorNode.getParent();
      if ($isLinkNode(linkNode)) {
        linkNode.select(0, linkNode.getChildrenSize());
        selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
      }

      const selectedText = selection.getTextContent();
      // Reuse the current selection as the link's text only when the author
      // didn't type a distinct display text. Otherwise insert (or replace the
      // selection with) a fresh text node carrying the display text.
      const wrapSelection =
        !selection.isCollapsed() && (!text || text === selectedText);
      if (!wrapSelection) {
        const display = text || selectedText || url;
        const node = $createTextNode(display);
        selection.insertNodes([node]);
        node.select(0, node.getTextContentSize());
      }
    });
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
  };

  const triggerImagePick = () => imageInputRef.current?.click();

  const handleImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) return;
    try {
      const { url } = await uploadTemplateImage(file);
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
        src: url,
        alt: file.name || "image",
      });
    } catch (err) {
      const msg =
        err?.error ||
        err?.detail ||
        err?.message ||
        "Image upload failed. Try a smaller PNG, JPG, or WEBP under 5MB.";
      if (onError) onError(msg);
      else if (typeof window !== "undefined") window.alert(msg);
    }
  };

  const insertVariable = (item) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const node = $createVariableNode(
          item.key,
          item.label,
          !!item.isSignature
        );
        selection.insertNodes([node]);
      }
    });
  };

  return (
    <div className="contract-toolbar">
      <div className="contract-toolbar-scroll">
        <button
          type="button"
          className={`contract-toolbar-btn ${isBold ? "active" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
          title="Bold (⌘B)"
        >
          <Icon path={mdiFormatBold} size={0.83} />
        </button>
        <button
          type="button"
          className={`contract-toolbar-btn ${isItalic ? "active" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
          title="Italic (⌘I)"
        >
          <Icon path={mdiFormatItalic} size={0.83} />
        </button>
        <button
          type="button"
          className={`contract-toolbar-btn ${isUnderline ? "active" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
          }
          title="Underline (⌘U)"
        >
          <Icon path={mdiFormatUnderline} size={0.83} />
        </button>
        <div className="contract-toolbar-divider" />
        <button
          type="button"
          className="contract-toolbar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() =>
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          }
          title="Bulleted list"
        >
          <Icon path={mdiFormatListBulleted} size={0.83} />
        </button>
        <button
          type="button"
          className="contract-toolbar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() =>
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          }
          title="Numbered list"
        >
          <Icon path={mdiFormatListNumbered} size={0.83} />
        </button>
        <button
          type="button"
          className="contract-toolbar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={openLinkModal}
          title="Insert link"
        >
          <Icon path={mdiLink} size={0.83} />
        </button>
        <button
          type="button"
          className="contract-toolbar-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={triggerImagePick}
          title="Insert image"
        >
          <Icon path={mdiImageOutline} size={0.83} />
        </button>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageSelected}
        />
      </div>
      <div className="contract-toolbar-insert">
        <VariableDropdown onInsert={insertVariable} />
      </div>
      <InsertLinkModal
        show={linkModalOpen}
        initialText={linkInitialText}
        onClose={() => setLinkModalOpen(false)}
        onInsert={handleInsertLink}
      />
    </div>
  );
};

export default ToolbarPlugin;
